/**
 * Data Export Formatters
 * Utility functions for formatting values during export
 */

import { format, parseISO, isValid } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import type { FormatOptions } from './types';

export const DEFAULT_FORMAT_OPTIONS: FormatOptions = {
  dateFormat: 'yyyy-MM-dd',
  datetimeFormat: 'yyyy-MM-dd HH:mm:ss',
  timezone: 'America/Chicago',
  nullValue: '',
  currencySymbol: '$',
  booleanTrue: 'Yes',
  booleanFalse: 'No',
};

/**
 * Format a date value for export
 */
export function formatDate(
  value: Date | string | null | undefined,
  options: Pick<FormatOptions, 'dateFormat' | 'timezone' | 'nullValue'>
): string {
  if (value === null || value === undefined) {
    return options.nullValue;
  }

  try {
    const date = typeof value === 'string' ? parseISO(value) : value;

    if (!isValid(date)) {
      return options.nullValue;
    }

    return formatInTimeZone(date, options.timezone, options.dateFormat);
  } catch {
    return options.nullValue;
  }
}

/**
 * Format a datetime value for export
 */
export function formatDateTime(
  value: Date | string | null | undefined,
  options: Pick<FormatOptions, 'datetimeFormat' | 'timezone' | 'nullValue'>
): string {
  if (value === null || value === undefined) {
    return options.nullValue;
  }

  try {
    const date = typeof value === 'string' ? parseISO(value) : value;

    if (!isValid(date)) {
      return options.nullValue;
    }

    return formatInTimeZone(date, options.timezone, options.datetimeFormat);
  } catch {
    return options.nullValue;
  }
}

/**
 * Format a currency value for export
 */
export function formatCurrency(
  value: number | null | undefined,
  options: Pick<FormatOptions, 'currencySymbol' | 'nullValue'>
): string {
  if (value === null || value === undefined) {
    return options.nullValue;
  }

  // Format with 2 decimal places
  const formatted = value.toFixed(2);
  return `${options.currencySymbol}${formatted}`;
}

/**
 * Format a boolean value for export
 */
export function formatBoolean(
  value: boolean | null | undefined,
  options: Pick<FormatOptions, 'booleanTrue' | 'booleanFalse' | 'nullValue'>
): string {
  if (value === null || value === undefined) {
    return options.nullValue;
  }

  return value ? options.booleanTrue : options.booleanFalse;
}

/**
 * Format a number value for export
 */
export function formatNumber(
  value: number | null | undefined,
  options: Pick<FormatOptions, 'nullValue'>,
  decimals?: number
): string {
  if (value === null || value === undefined) {
    return options.nullValue;
  }

  if (decimals !== undefined) {
    return value.toFixed(decimals);
  }

  return String(value);
}

/**
 * Format a JSON value for export
 */
export function formatJson(
  value: unknown,
  options: Pick<FormatOptions, 'nullValue'>
): string {
  if (value === null || value === undefined) {
    return options.nullValue;
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return options.nullValue;
    }
  }

  return String(value);
}

/**
 * Format a string value for export (handles null/undefined)
 */
export function formatString(
  value: string | null | undefined,
  options: Pick<FormatOptions, 'nullValue'>
): string {
  if (value === null || value === undefined) {
    return options.nullValue;
  }

  return String(value);
}

/**
 * Format any value based on its type
 */
export function formatValue(
  value: unknown,
  type: 'string' | 'number' | 'date' | 'datetime' | 'boolean' | 'json' | 'currency',
  options: FormatOptions
): string {
  switch (type) {
    case 'date':
      return formatDate(value as Date | string | null, options);
    case 'datetime':
      return formatDateTime(value as Date | string | null, options);
    case 'boolean':
      return formatBoolean(value as boolean | null, options);
    case 'number':
      return formatNumber(value as number | null, options);
    case 'currency':
      return formatCurrency(value as number | null, options);
    case 'json':
      return formatJson(value, options);
    case 'string':
    default:
      return formatString(value as string | null, options);
  }
}

/**
 * Generate a filename for the export
 */
export function generateFilename(
  entities: string[],
  format: 'csv' | 'json' | 'pdf',
  clientName?: string
): string {
  const timestamp = format(new Date(), 'yyyy-MM-dd_HHmmss');

  let baseName: string;
  if (clientName) {
    // Sanitize client name for filename
    const sanitized = clientName
      .replace(/[^a-zA-Z0-9\s-]/g, '')
      .replace(/\s+/g, '_')
      .substring(0, 30);
    baseName = `${sanitized}_data`;
  } else if (entities.length === 1) {
    baseName = entities[0];
  } else {
    baseName = 'client_pulse_export';
  }

  return `${baseName}_${timestamp}.${format}`;
}

/**
 * Sanitize a string for use in filenames
 */
export function sanitizeFilename(name: string): string {
  return name
    .replace(/[^a-zA-Z0-9\s\-_.]/g, '')
    .replace(/\s+/g, '_')
    .substring(0, 50);
}
